import {
  Box,
  Image,
  Tag,
  Aperture,
  Fan,
  AirVent,
  Hammer,
  Wind,
  Cog,
  Filter,
  Wrench,
  Zap,
  Thermometer,
  Gauge,
  CircleAlert,
  Link,
  Beaker,
  Droplet,
  Usb,
  Cable,
  Route,
  SeparatorHorizontal,
  SignalHigh,
  FlaskRound,
  ArrowUp,
  Cylinder,
} from 'lucide-react'
import ReactDOMServer from 'react-dom/server'
import { parseName } from '@/service/graph'

const iconToDataUri = (
  Icon: React.ComponentType<{ size?: number | string; color?: string; strokeWidth?: number | string }>
) => {
  const svg = ReactDOMServer.renderToStaticMarkup(
    <Icon size={14} color="#4b5563" strokeWidth={2} />
  )
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

const BOX_URI = iconToDataUri(Box)
const IMAGE_URI = iconToDataUri(Image)
const TAG_URI = iconToDataUri(Tag)
const APERTURE_URI = iconToDataUri(Aperture)
const GAUGE_URI = iconToDataUri(Gauge)
const FAN_URI = iconToDataUri(Fan)
const AIRVENT_URI = iconToDataUri(AirVent)
const HAMMER_URI = iconToDataUri(Hammer)
const WIND_URI = iconToDataUri(Wind)
const COG_URI = iconToDataUri(Cog)
const FILTER_URI = iconToDataUri(Filter)
const WRENCH_URI = iconToDataUri(Wrench)
const ZAP_URI = iconToDataUri(Zap)
const THERMOMETER_URI = iconToDataUri(Thermometer)
const CIRCLEALERT_URI = iconToDataUri(CircleAlert)
const LINK_URI = iconToDataUri(Link)
const BEAKER_URI = iconToDataUri(Beaker)
const DROPLET_URI = iconToDataUri(Droplet)
const USB_URI = iconToDataUri(Usb)
const CABLE_URI = iconToDataUri(Cable)
const ROUTE_URI = iconToDataUri(Route)
const SEPARATOR_H_URI = iconToDataUri(SeparatorHorizontal)
const SIGNALHIGH_URI = iconToDataUri(SignalHigh)
const FLASKROUND_URI = iconToDataUri(FlaskRound)
const ARROW_URI = iconToDataUri(ArrowUp)
const CYLINDER_URI = iconToDataUri(Cylinder)

const ICON_CACHE = new Map<string, string>()

export const getNodeIconUri = (
  _id: string,
  name?: string | null,
  type?: string | null
): string | null => {
  const lowerName = (name || '').toLowerCase()
  const categoryLower = name ? (parseName(name).category || '').toLowerCase() : ''
  const typeLower = (type || '').toLowerCase()
  const searchText = `${lowerName} ${categoryLower}`.trim()
  const cacheKey = `${searchText}|${typeLower}`

  const cached = ICON_CACHE.get(cacheKey)
  if (cached) return cached

  // Extract prefix from name for direct mapping (e.g., "pum_" from "PUM_Centrifugal_Pump")
  const getPrefix = (text: string): string | null => {
    const match = text.match(/^([a-z]{2,4})_/i)
    return match ? match[1].toLowerCase() : null
  }

  const prefix = getPrefix(lowerName)

  // Direct prefix-to-icon mapping (more reliable than keyword search)
  const CATEGORY_ICON_MAP: Record<string, string> = {
    'ino': ROUTE_URI,        // Inlet/Outlet
    'ves': FLASKROUND_URI,   // Vessels
    'val': APERTURE_URI,     // Valves
    'pum': DROPLET_URI,      // Pumps
    'pic': CABLE_URI,        // Piping Components
    'per': USB_URI,          // Peripheral Equipment
    'mot': COG_URI,          // Motors
    'mix': BEAKER_URI,       // Mixers
    'ins': GAUGE_URI,        // Instruments
    'htx': THERMOMETER_URI,  // Heat Exchangers
    'gen': ZAP_URI,          // Generators
    'fil': FILTER_URI,       // Filters
    'dry': WIND_URI,         // Dryers
    'cru': HAMMER_URI,       // Crushers
    'com': AIRVENT_URI,      // Compressors
    'cen': FAN_URI,          // Centrifuges
    'sig': SIGNALHIGH_URI,   // Signals
    'pip': ROUTE_URI,        // Piping
  }

  // Try prefix-based matching first
  if (prefix && CATEGORY_ICON_MAP[prefix]) {
    const iconUri = CATEGORY_ICON_MAP[prefix]
    ICON_CACHE.set(cacheKey, iconUri)
    return iconUri
  }

  const uri = (() => {
    if (typeLower === 'connection') return ROUTE_URI
    if (searchText.includes('connection')) return ROUTE_URI
    if (searchText.includes('pipe')) return ROUTE_URI
    if (searchText.includes('electric')) return CABLE_URI
    if (searchText.includes('piping and connection')) return CABLE_URI
    if (searchText.includes('piping')) return ROUTE_URI
    if (searchText.includes('centrifug')) return FAN_URI
    if (searchText.includes('compress')) return AIRVENT_URI
    if (searchText.includes('crush')) return HAMMER_URI
    if (searchText.includes('dryer') || searchText.includes('dry')) return WIND_URI
    if (searchText.includes('equipment')) return COG_URI
    if (searchText.includes('filter')) return FILTER_URI
    if (searchText.includes('fitting')) return WRENCH_URI
    if (searchText.includes('generat')) return ZAP_URI
    if (searchText.includes('heat exchanger')) return THERMOMETER_URI
    if (searchText.includes('instrument')) return GAUGE_URI
    if (searchText.includes('indicator')) return CIRCLEALERT_URI
    if (searchText.includes('link')) return LINK_URI
    if (searchText.includes('mixer') || searchText.includes('mix')) return BEAKER_URI
    if (searchText.includes('motor')) return COG_URI
    if (searchText.includes('pump')) return DROPLET_URI
    if (searchText.includes('periph')) return USB_URI
    if (searchText.includes('pump')) return DROPLET_URI
    if (searchText.includes('periph')) return USB_URI
    if (searchText.includes('separa')) return SEPARATOR_H_URI
    if (searchText.includes('signal')) return SIGNALHIGH_URI
    if (searchText.includes('valv')) return APERTURE_URI
    if (searchText.includes('vessel') || searchText.includes('ves')) return FLASKROUND_URI
    if (searchText.includes('arrow')) return ARROW_URI
    if (searchText.includes('tank')) return CYLINDER_URI
    if (searchText.includes('general')) return BOX_URI
    if (searchText.includes('icon')) return IMAGE_URI
    return TAG_URI
  })()

  ICON_CACHE.set(cacheKey, uri)
  return uri
}


